import React, { useState, useEffect, useLayoutEffect, useRef } from 'react';
import '../../styles/student/Sidebar.css';
import { getAvatarImage, getUserProfileData } from '../../utils/avatarUtils';

const Sidebar = () => {
  const [showMessagePopup, setShowMessagePopup] = useState(false);
  const [selectedOption, setSelectedOption] = useState('');
  const [messageText, setMessageText] = useState('');
  const [userProfile, setUserProfile] = useState({
    favoriteAnimal: '',
    username: '',
    displayUsername: '',
    name: '',
    grade: '',
    schoolName: ''
  });
  const [avatarImage, setAvatarImage] = useState(null);
  const funNameRef = useRef(null);

  // Keep the @username on one line: start at the CSS size and shrink until it fits the profile box.
  useLayoutEffect(() => {
    const el = funNameRef.current;
    if (!el) return;
    const fit = () => {
      el.style.fontSize = '';
      const minPx = 12;
      let size = parseFloat(getComputedStyle(el).fontSize);
      while (el.scrollWidth > el.clientWidth && size > minPx) {
        size -= 1;
        el.style.fontSize = `${size}px`;
      }
    };
    fit();
    // Re-fit once web fonts load, since they change the text width.
    document.fonts?.ready.then(fit);
  }, [userProfile.displayUsername]);

  // Load user profile data and set avatar
  useEffect(() => {
    console.log('🔍 Loading user profile data...');
    const profileData = getUserProfileData();
    console.log('📊 Profile data loaded:', profileData);
    console.log('💾 localStorage contents:', {
      studentProfileData: localStorage.getItem('studentProfileData'),
      signupUsername: localStorage.getItem('signupUsername'),
      signupName: localStorage.getItem('signupName'),
      favoriteAnimal: localStorage.getItem('favoriteAnimal'),
      currentGrade: localStorage.getItem('currentGrade'),
      schoolName: localStorage.getItem('schoolName')
    });
    
    // Debug: Check if studentProfileData contains name
    const studentData = localStorage.getItem('studentProfileData');
    if (studentData) {
      try {
        const parsed = JSON.parse(studentData);
        console.log('🔍 Parsed studentProfileData:', parsed);
        console.log('🔍 Name field in studentProfileData:', parsed.name);
      } catch (e) {
        console.error('❌ Error parsing studentProfileData:', e);
      }
    }
    
    setUserProfile(profileData);
    setAvatarImage(getAvatarImage(profileData.favoriteAnimal));
  }, []);

  // Listen for localStorage changes to refresh profile data
  useEffect(() => {
    const handleStorageChange = () => {
      console.log('🔄 localStorage changed, refreshing profile...');
      const profileData = getUserProfileData();
      setUserProfile(profileData);
      setAvatarImage(getAvatarImage(profileData.favoriteAnimal));
    };

    // Listen for storage events (when localStorage is updated from another tab/component)
    window.addEventListener('storage', handleStorageChange);
    
    // Also listen for custom events (when localStorage is updated in same tab)
    window.addEventListener('profileUpdated', handleStorageChange);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('profileUpdated', handleStorageChange);
    };
  }, []);

  const handleMessageClick = () => {
    // TODO: Re-enable Message Hub functionality
    // setShowMessagePopup(true);
    console.log('Message Hub button clicked (functionality disabled)');
  };

  const handleProfileClick = () => {
    console.log('Profile clicked!');
    // Add your portfolio functionality here
  };

  const handleSubmitMessage = () => {
    console.log('Message submitted:', { option: selectedOption, text: messageText });
    // Add your message submission logic here
    setShowMessagePopup(false);
    setSelectedOption('');
    setMessageText('');
  };

  const handleClosePopup = () => {
    setShowMessagePopup(false);
    setSelectedOption('');
    setMessageText('');
  };

  // Panda SVG component
  const PandaImage = () => (
    <svg width="200" height="200" viewBox="0 0 200 200" fill="none" xmlns="http://www.w3.org/2000/svg">
      {/* Panda body - larger to fill the circle */}
      <circle cx="100" cy="100" r="85" fill="white" stroke="black" strokeWidth="3"/>
      
      {/* Panda ears - larger and positioned better */}
      <circle cx="65" cy="55" r="20" fill="black"/>
      <circle cx="135" cy="55" r="20" fill="black"/>
      
      {/* Panda eye patches - larger and more prominent */}
      <ellipse cx="75" cy="90" rx="15" ry="18" fill="black"/>
      <ellipse cx="125" cy="90" rx="15" ry="18" fill="black"/>
      
      {/* Panda eyes - larger with better positioning */}
      <circle cx="72" cy="85" r="5" fill="white"/>
      <circle cx="128" cy="85" r="5" fill="white"/>
      <circle cx="70" cy="83" r="2.5" fill="black"/>
      <circle cx="126" cy="83" r="2.5" fill="black"/>
      <circle cx="69" cy="82" r="1" fill="white"/>
      <circle cx="125" cy="82" r="1" fill="white"/>
      
      {/* Panda nose - larger */}
      <ellipse cx="100" cy="110" rx="3" ry="2.5" fill="black"/>
      
      {/* Panda mouth - larger and more prominent */}
      <path d="M 90 120 Q 100 130 110 120" stroke="black" strokeWidth="2.5" fill="none"/>
      
      {/* Panda cheeks - larger and more visible */}
      <circle cx="55" cy="115" r="10" fill="#FFB6C1" opacity="0.7"/>
      <circle cx="145" cy="115" r="10" fill="#FFB6C1" opacity="0.7"/>
      
      {/* Additional cute details - inner ear highlights */}
      <circle cx="68" cy="58" r="6" fill="white" opacity="0.3"/>
      <circle cx="132" cy="58" r="6" fill="white" opacity="0.3"/>
      
      {/* Sparkles - larger and more prominent */}
      <g fill="black">
        <path d="M 40 40 L 45 40 L 42.5 35 L 40 40 Z"/>
        <path d="M 160 40 L 165 40 L 162.5 35 L 160 40 Z"/>
        <path d="M 40 160 L 45 160 L 42.5 165 L 40 160 Z"/>
        <path d="M 160 160 L 165 160 L 162.5 165 L 160 160 Z"/>
        <path d="M 30 100 L 35 100 L 32.5 95 L 30 100 Z"/>
        <path d="M 170 100 L 175 100 L 172.5 95 L 170 100 Z"/>
      </g>
    </svg>
  );

  return (
    <div className="sidebar-container">
      <div className="profile-section">
        <div className="profile-window" onClick={handleProfileClick}>
          <div className="panda-container">
            {avatarImage && (
              <img 
                src={avatarImage} 
                alt={`${userProfile.favoriteAnimal || 'Animal'} Profile`} 
                className="panda-image" 
              />
            )}
          </div>
        </div>
        {userProfile.displayUsername && (
          <div className="student-fun-name" ref={funNameRef}>@{userProfile.displayUsername}</div>
        )}
        <div className="student-name">{
          (() => {
            const displayName = userProfile.name || localStorage.getItem('signupName') || userProfile.username || localStorage.getItem('signupUsername') || 'Student';
            console.log('🏷️ Display name resolved:', {
              userProfileName: userProfile.name,
              signupName: localStorage.getItem('signupName'),
              userProfileUsername: userProfile.username,
              signupUsername: localStorage.getItem('signupUsername'),
              finalDisplayName: displayName
            });
            return displayName;
          })()
        }</div>
        <div className="student-details">
          <div className="student-grade" style={{fontSize: '1.5rem', color: '#D4C4A8'}}>{userProfile.grade || localStorage.getItem('currentGrade') || ''} Grade</div>
          <div className="student-school">{userProfile.schoolName || 'School'}</div>
        </div>
      </div>
      {/*}
      <button className="message-button app-btn" onClick={handleMessageClick}>
        MESSAGE<br />HUB
      </button>
      <div className="teacher-hub">Message your Teacher<br />or Avatar for help</div>

      
      {showMessagePopup && (
        <div className="message-popup-overlay" onClick={handleClosePopup}>
          <div className="message-popup" onClick={e => e.stopPropagation()}>
            <button className="message-popup-close" onClick={handleClosePopup}>&times;</button>
            <div className="message-popup-content">
              <h3 className="message-popup-title">📊 Message Hub</h3>
              <p className="message-popup-subtitle">How can we help you today?</p>
              
              <div className="message-options">
                <label className="message-option">
                  <input
                    type="radio"
                    name="messageType"
                    value="help"
                    checked={selectedOption === 'help'}
                    onChange={(e) => setSelectedOption(e.target.value)}
                  />
                  <span className="option-text">I need help with my studies</span>
                </label>
                
                <label className="message-option">
                  <input
                    type="radio"
                    name="messageType"
                    value="question"
                    checked={selectedOption === 'question'}
                    onChange={(e) => setSelectedOption(e.target.value)}
                  />
                  <span className="option-text">I have a question</span>
                </label>
                
                <label className="message-option">
                  <input
                    type="radio"
                    name="messageType"
                    value="feedback"
                    checked={selectedOption === 'feedback'}
                    onChange={(e) => setSelectedOption(e.target.value)}
                  />
                  <span className="option-text">I want to give feedback</span>
                </label>
                
                <label className="message-option">
                  <input
                    type="radio"
                    name="messageType"
                    value="other"
                    checked={selectedOption === 'other'}
                    onChange={(e) => setSelectedOption(e.target.value)}
                  />
                  <span className="option-text">Something else</span>
                </label>
              </div>
              
              <div className="message-textarea-container">
                <label htmlFor="message-textarea" className="message-textarea-label">
                  Your Message:
                </label>
                <textarea
                  id="message-textarea"
                  className="message-textarea"
                  value={messageText}
                  onChange={(e) => setMessageText(e.target.value)}
                  placeholder="Type your message here..."
                  rows={4}
                />
              </div>
              
              <div className="message-popup-buttons">
                <button 
                  className="message-popup-cancel app-btn" 
                  onClick={handleClosePopup}
                >
                  Cancel
                </button>
                <button 
                  className="message-popup-send app-btn" 
                  onClick={handleSubmitMessage}
                  disabled={!selectedOption || !messageText.trim()}
                >
                  Send Message
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      */}
    </div>
  );
};

export default Sidebar; 